import React from 'react';
import LoadingBanner from "../../../components/LoadingBanner";
import {Empty, Switch, Table} from "antd";
import ErrorBanner from "../../../components/ErrorBanner";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useAPI} from "../../../config/API";
import {useProjectForm} from "../../../providers/ProjectFormProvider";
import {CheckOutlined, CloseOutlined} from "@ant-design/icons";
import UserImageCircle from "../../../components/UserImageCircle";
import TalentCandidate from "../../../models/TalentCandidate";
import TalentCandidateScopeProvider, {TalentCandidateScopeContext} from "../candidate-scope/TalentCandidateScopeProvider";
import {useIsMutating} from "@tanstack/react-query";
import {ProjectPositionProvider} from "../../../providers/ProjectPositionProvider";

const PositionCandidatesTable = ({position, forAgency, ...props}) => {
    const {formModel: project, isProjectUpdating} = useProjectForm();

    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();
    const hasBeenFilled = position?.assigned_candidate?.id;
    const {
        data: candidateData,
        isFetching,
        isInitialLoading: isLoading,
        isRefetching,
        error,
        refetch
    } = useAPI(['positionMatches', {
            project_position: position.id,
            accomplished_disciplines: TB.map(position.disciplines, 'id'),
            shortlisted: 1,
            interested: 1,
            per_page: 500,
            // capture_candidates_request: 1
        }], {
            select: data => {
                if (data?.users?.length) {
                    let users = TB.map(data.users, user => TalentCandidate.normalizeScopeForPosition(user, position));
                    users = TB.orderBy(users, user => {
                        if (user.declinedPosition()) return 0;
                        if (user.isAssigned()) return 4;
                        if (user.statusIsApproved()) return 3;
                        if (user.matchStatusIsInterested()) return 2;
                        if (user.statusIsPending()) return 1;

                        return 1;
                    }, 'desc')

                    return {
                        ...data, users
                    }
                }
                return data;
            }
        }
    )
    const candidateRequestsRunning = useIsMutating({
        mutationKey: 'updateCandidateScope',
        exact: false,
        fetching: true
    })
    const columnWidths = {
        Name: 210,
        // Roles: 150,
        Invited: 80,
        Type: 80,
        Scoping: 100,
        Status: 220,
        // Talent_Hours: 125,
        // Talent_Rate: 125,
        // Talent_Payout: 125,
        Payout: 150,
        Actions: 105,
    }
    return (
        <div>

            {isLoading ? (
                <LoadingBanner message="Loading Candidate Invites"/>
            ) : (error ? (
                <ErrorBanner/>
            ) : (
                candidateData?.users?.length > 0 ? (
                    <div className="relative w-full py-8">
                        <Table
                            rowKey="id"
                            dataSource={candidateData?.users}
                            bordered
                            size="middle"
                            scroll={{
                                x: TB.sum(TB.values(columnWidths)),
                            }}
                            pagination={false}>


                            <Table.Column
                                title="Candidate"
                                dataIndex="name"
                                width={columnWidths.Name}
                                // fixed={"left"}
                                editable={false}
                                render={(_, record) => {
                                    return (
                                        <div className="flex flex-row items-center gap-2">
                                            <UserImageCircle
                                                user={record} size={28}
                                                className="img-fluid img-grayscale border border-white"/>
                                            <p className="w-44 text-sm font-semibold flex flex-row items-center gap-3">
                                                {record.fullName?.()}
                                            </p>
                                        </div>
                                    )
                                }}
                            />
                            {/*<Table.Column*/}
                            {/*    title="Related Roles"*/}
                            {/*    dataIndex="disciplines"*/}
                            {/*    key="disciplines"*/}
                            {/*    width={columnWidths.Roles}*/}
                            {/*    render={(_, record) => (*/}
                            {/*        <div className="flex flex-col items-start gap-2">*/}
                            {/*            {TB.map(record?.matching?.disciplines?.disciplines || [],(userDiscipline) => {*/}
                            {/*                let discipline = UserDiscipline.storedDiscipline(userDiscipline.discipline_id,true);*/}
                            {/*                if (!discipline?.name) return null;*/}
                            {/*                return (*/}
                            {/*                    <div*/}
                            {/*                        key={`${record.id}-roles-${discipline.id}`}*/}
                            {/*                        className="h-4 flex flex-row items-center justify-start gap-1.5">*/}
                            {/*                        <Tag color="purple">*/}
                            {/*                            {discipline.name}*/}
                            {/*                        </Tag>*/}
                            {/*                    </div>*/}
                            {/*                )*/}
                            {/*            })}*/}
                            {/*        </div>*/}
                            {/*    )}*/}
                            {/*/>*/}
                            <Table.Column
                                title="Invited At"
                                dataIndex="job_candidate.talent_invited_at"
                                key="job_candidate.talent_invited_at"
                                width={columnWidths.Invited}
                                render={(_, record) => (
                                    <p className="text-xxs font-medium">
                                        {record.job_candidate?.talent_invited_at ? (
                                            <span className="text-secondary">
                                                {TB.moment(record.job_candidate?.talent_invited_at).format('ll')}
                                            </span>
                                        ) : (
                                            <i>Interested: {TB.moment(record.job_candidate?.talent_proposed_at).format('ll')}</i>
                                        )}
                                    </p>
                                )}
                            />
                            <Table.Column
                                title="Type"
                                dataIndex="supplier_client"
                                width={columnWidths.Type}
                                align="center"
                                editable={false}
                                render={supplier_client => {
                                    return (
                                        <p className="text-xxs text-secondary font-medium">
                                            {supplier_client?.id ? 'Agency Resource' : 'Freelancer'}
                                        </p>
                                    )
                                }}
                            />
                            <Table.Column
                                title="Payout Confirmed"
                                align="center"
                                dataIndex="job_candidate.payout_confirmed_at"
                                width={columnWidths.Scoping}
                                editable={false}
                                render={(_, record) => {
                                    return (
                                        <ProjectPositionProvider positionId={position.id}>
                                            <TalentCandidateScopeProvider candidate={record} >
                                                <TalentCandidateScopeContext.Consumer>
                                                    {({candidate, isLoading, confirmCandidateScope, isUpdating}) => {
                                                    if (isLoading || !candidate) {
                                                        return null
                                                    }
                                                    return (
                                                        <div className={'flex flex-row items-center gap-1.5 ' + (!isProjectUpdating ? '' : 'pointer-events-none')}>
                                                            <Switch
                                                                size="small"
                                                                className={record.hasConfirmedPayout() ? 'bg-green-400' : ''}
                                                                checkedChildren={<CheckOutlined/>}
                                                                unCheckedChildren={<CloseOutlined/>}
                                                                checked={record.hasConfirmedPayout()}
                                                                disabled={isUpdating || candidateRequestsRunning > 0}
                                                                onChange={async (checked) => {
                                                                    console.log('ischeck', checked)
                                                                    if (checked) {
                                                                        return confirmCandidateScope()
                                                                    }
                                                                }}
                                                            />
                                                            <p className={TB.mapClass("whitespace-nowrap text-xs", {
                                                                'font-semibold': !record.hasConfirmedPayout()
                                                            })}>
                                                                {record.job_candidate?.has_confirmed_payout ? 'Confirmed' : 'Pending'}
                                                            </p>
                                                        </div>
                                                    )
                                                }}
                                                </TalentCandidateScopeContext.Consumer>
                                            </TalentCandidateScopeProvider>
                                        </ProjectPositionProvider>
                                    )
                                }}
                            />


                            <Table.Column
                                title="Member Status"
                                dataIndex="job_candidate"
                                width={columnWidths.Status}
                                editable={false}
                                render={(job_candidate, record, index) => {

                                    let [statusLabel, statusColor] = record.scopeStatusForPosition(position);

                                    return (
                                        <div key={record.id} className="flex flex-row gap-1 items-center flex-nowrap">
                                            <div style={{background: statusColor}} className="h-3 w-6"/>

                                            <p className="text-xxs">
                                                {statusLabel}
                                            </p>
                                        </div>
                                    )
                                }}
                            />
                            <Table.Column
                                title="Talent Payout / Client Fee"
                                dataIndex="disciplines"
                                editable={false}
                                width={columnWidths.Payout}
                                render={(_, record, index) => {


                                    return (
                                        <div className="flex flex-row gap-3 items-center">
                                            <p className="text-sm whitespace-nowrap">
                                                <React.Fragment>
                                                    {TB.formatCurrency(record.job_candidate?.payout_amount, false)}
                                                    &nbsp;/&nbsp;
                                                    {TB.formatCurrency(record.job_candidate?.platform_cost, false)}
                                                </React.Fragment>
                                            </p>
                                        </div>
                                    )
                                }}
                            />
                            <Table.Column
                                title="Actions"
                                align="center"
                                dataIndex="operations"
                                width={columnWidths.Actions}
                                fixed="right"
                                render={(_, record) => {

                                    if (position.hasBeenFilled() && record.job_candidate?.id !== position.assigned_candidate?.job_candidate?.id) {
                                        return null;
                                    }
                                    return (
                                        <button className="btn btn-info text-xxs whitespace-nowrap py-1 px-1.5"
                                                disabled={isProjectUpdating}
                                                onClick={() => {
                                                    navigate(`${record.job_candidate?.id}`)
                                                }}>
                                            Manage Scope
                                        </button>
                                    )
                                }}
                            />
                        </Table>
                    </div>
                ) : (
                    <Empty description="No candidates have been invited by the client to propose"
                           image={Empty.PRESENTED_IMAGE_SIMPLE}/>
                )
            ))}
        </div>
    );
};

export default PositionCandidatesTable;